import React from 'react';
import { Module } from '../../types/domain';

interface ModuleGridProps {
  modules: Module[];
  selectedModules: string[];
  onModuleToggle: (moduleId: string) => void;
}

interface ModuleCategory {
  name: string;
  modules: Module[];
}

const formatName = (name: string) => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ModuleGrid: React.FC<ModuleGridProps> = ({
  modules,
  selectedModules,
  onModuleToggle,
}) => {
  // Group modules by category from metadata
  const categories = modules.reduce((acc, module) => {
    const category = (module.metadata?.category as string) || 'Other';
    if (!acc[category]) {
      acc[category] = {
        name: category,
        modules: [],
      };
    }
    acc[category].modules.push(module);
    return acc;
  }, {} as Record<string, ModuleCategory>);

  // Sort categories to ensure consistent order (Oncology first, then Chronic Diseases)
  const sortedCategories = Object.entries(categories).sort(([a], [b]) => {
    if (a === 'oncology') return -1;
    if (b === 'oncology') return 1;
    if (a === 'chronic_diseases') return -1;
    if (b === 'chronic_diseases') return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-8">
      {sortedCategories.map(([key, category]) => (
        <div key={key} className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {formatName(category.name)}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {category.modules.map((module) => (
              <div
                key={module.moduleId}
                className={`relative flex items-start p-4 rounded-lg border ${
                  selectedModules.includes(module.moduleId)
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                } cursor-pointer transition-colors duration-200`}
                onClick={() => onModuleToggle(module.moduleId)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      checked={selectedModules.includes(module.moduleId)}
                      onChange={() => onModuleToggle(module.moduleId)}
                    />
                    <label className="ml-3 block text-sm font-medium text-gray-900">
                      {formatName(module.name)}
                    </label>
                  </div>
                  {module.description && (
                    <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                      {module.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ModuleGrid;
